import { useMemo } from 'react'
import { Link } from 'react-router-dom-v5-compat'

import {
  Avatar,
  ChannelAvatar,
  type ChannelAvatarProps,
  Text,
} from '@buffer-mono/popcorn'

import { mapPostStatusToTab } from '~publish/helpers/post'
import { isOfType } from '~publish/helpers/typeGuards'
import { channel as channelPage } from '~publish/legacy/routes'

import {
  usePostData,
  useMetadataThreadsTopic,
} from '../PostCard/PostCardContext'

import styles from './ChannelInfo.module.css'

export const ChannelInfo = (): JSX.Element | null => {
  const { status, channel, metadata } = usePostData()
  const threadsTopic = useMetadataThreadsTopic(metadata)

  const channelLink = channelPage.getRoute(channel.id, {
    tab: mapPostStatusToTab(status),
  })

  const channelInfo = useMemo(() => {
    const serverUrl = channel.serverUrl

    if (serverUrl || channel.service !== 'facebook') {
      return serverUrl
    }

    if (channel.locationData?.location) {
      return channel.locationData.location
    }

    return null
  }, [channel])

  const pinterestBoard =
    isOfType(metadata, 'PinterestPostMetadata') && metadata.board

  const hasSecondaryInfo = channelInfo || pinterestBoard

  return (
    <div className={styles.container}>
      <Link to={channelLink} className={styles.avatar}>
        <ChannelAvatar
          size="small"
          alt={`${channel.name}, ${channel.service}`}
          src={channel.avatar ?? undefined}
          channel={channel.service as ChannelAvatarProps['channel']}
        />
      </Link>

      <div className={styles.info}>
        <Text weight="bold" className={styles.name} lineHeight="tight" truncate>
          {channel.name}
          {threadsTopic && channel.service === 'threads' && (
            <>
              <span className={styles.topicSeparator}>&gt;</span>
              <span className={styles.topic}>{threadsTopic}</span>
            </>
          )}
        </Text>
        {hasSecondaryInfo && (
          <Text
            color="subtle"
            size="sm"
            className={styles.secondaryInfo}
            truncate
          >
            {channelInfo}
            {channelInfo && pinterestBoard && <span aria-hidden>•</span>}
            {pinterestBoard && (
              <span className={styles.pinterestBoard}>
                <Avatar
                  size="xsmall"
                  src={metadata.board?.avatar ?? undefined}
                  alt={metadata.board?.name || ''}
                />
                {metadata.board?.name}
              </span>
            )}
          </Text>
        )}
      </div>
    </div>
  )
}
